import { FC, useState } from 'react';
import Drawer from '..';
import DrawerHeader from '../header';
import DrawerContent from '../content';
import { useQuery } from 'react-query';
import { ViewState } from '@/lib/helpers';
import useQueryHelper from '@/hooks/use-query-helper';
import useFormHandle from '@/hooks/use-form-handle.hook';
import ReadonlyView from '../../form/readonly-view';
import InputControl from '../../form/input-control';
import useToast from '@/hooks/use-toast.hook';
import useModal from '@/hooks/use-modal.hook';
import DrawerButtons from '@/components/form/drawer-buttons';
import { ReadonlyField } from '@/components/form/readonly-view/helpers';
import { QueryKey } from '@/lib/query-client';
import { Form } from '@/components/form';
import DrawerMenu from '../menu';
import UpdateMenuItems from '../menu/update-menu-items';
import { Box, Stack } from '@mui/material';
import useApp from '@/hooks/use-app.hook';
import { userCan } from '@/lib/services/auth.service';
import { OrgMode, OrgModel } from '@/lib/models/org.model';
import Chip from '@/components/chip';
import {
  createPartner,
  deletePartner,
  getPartnerById,
  patchPartner,
} from '@/lib/services/partner.service';
import { PartnerModel, PartnerOrgModeSettings } from '@/lib/models/partner.model';
import { DrawerFormSection } from '../form-section';
import Text from '@/components/text';
import InlineToast from '@/components/toasts/inline';
import { getOrgsByPartnerId } from '@/lib/services/org.service';
import ListCard from '@/components/list-card';
import { FieldError } from '@/lib/services';
import { DrawerProps } from '../helpers';
import OrgModesMatrix from './org-modes-matrix';
import MembersPanel from './members-panel';
import RouterTabs from '@/components/router-tabs';
import { TabPanel } from '@/components/router-tabs/helpers';
import { getPartnerMembers } from '@/lib/services/partner-user.service';

interface Props extends DrawerProps {
  id: string | null;
}

// Org permissions are granted by root and mean nothing to a partner's own users, so only root sees them.
const ROOT_ONLY_PROPS = new Set(['orgModes']);

const allFields: ReadonlyField[] = [
  { label: 'Name', prop: 'name' },
  { label: 'Domains', prop: 'domains' },
  { label: 'Organization provisioning permissions', prop: 'orgModes' },
];

const formModel = new PartnerModel();

const getReadonlyData = (data: PartnerModel | null) => {
  if (!data) {
    return {};
  }

  const { name, domains, features } = data;

  return {
    name,
    orgModes: <OrgModesMatrix orgModes={features.orgModes} />,
    domains: (
      <Stack gap={0.5} direction="row">
        {domains.map((domain) => (
          <Chip key={domain} label={domain} />
        ))}
      </Stack>
    ),
  };
};

const PartnerDrawer: FC<Props> = ({ id, open, onClose, onChange, className, isTopDrawer }) => {
  const [partnerId, setPartnerId] = useState(id || null);
  const isNew = !partnerId;
  const [mode, setMode] = useState<ViewState>(isNew ? 'add' : 'view');

  const { toast, errorToast } = useToast();
  const { openModal } = useModal();
  const { user } = useApp();
  const isSuper = userCan(user, 'super');
  const fields = allFields.filter(({ prop }) => isSuper || !ROOT_ONLY_PROPS.has(prop));

  const query = useQuery<[PartnerModel | null, OrgModel[]]>(
    [QueryKey.PartnerView, partnerId],
    async () => {
      const partner = await getPartnerById(partnerId!);

      if (!partner) {
        return [null, []];
      }

      const partnerOrgs = await getOrgsByPartnerId(partnerId!);

      return [partner, partnerOrgs];
    },
    {
      enabled: !isNew,
    }
  );

  const { showChildren, showLoader } = useQueryHelper(query);
  const { data, refetch } = query;
  const [partner, partnerOrgs] = data || [null, []];

  const membersQuery = useQuery(
    [QueryKey.PartnerMembers, partnerId],
    async () => getPartnerMembers(partnerId!),
    { enabled: !isNew && isSuper }
  );
  const members = membersQuery.data || [];

  const formHandle = useFormHandle({
    initialValues: formModel,
    validationSchema: formModel.schema,
    validateOnChange: !isNew,
    onSubmit: async (partner, { setFieldError }) => {
      const setFieldErrors = (fieldErrors: FieldError[]) => {
        fieldErrors.forEach(({ msg, path }) => setFieldError(path, msg));
      };

      if (!partnerId) {
        const response = await createPartner(partner, isSuper);

        const { created, model, error, fieldErrors } = response;

        if (created) {
          toast('The partner was created');
          setPartnerId(model!.id);
          onChange?.();
          setMode('view');
          return;
        }

        if (fieldErrors) {
          setFieldErrors(fieldErrors);
          return;
        }

        errorToast(error || 'The partner was not created');
        return;
      }

      const { patched, error, fieldErrors } = await patchPartner(partner, isSuper);

      if (patched) {
        toast('The partner was updated');
        refetch();
        onChange?.();
        setMode('view');
        return;
      }

      if (fieldErrors) {
        setFieldErrors(fieldErrors);
        return;
      }

      errorToast(error || 'The partner was not updated');
    },
  });

  const { handleSubmit, canSubmit, isSubmitting, loadData, setFieldValue, values } = formHandle;

  const handleChangeOrgMode = (
    orgMode: OrgMode,
    key: keyof PartnerOrgModeSettings,
    value: boolean
  ) => {
    setFieldValue(`features.orgModes.${orgMode}.${key}`, value);
  };

  const inWriteMode = ['edit', 'add'].includes(mode);

  const handleCancel = () => {
    if (isNew) {
      onClose();
      return;
    }
    setMode('view');
    loadData(partner!);
  };

  const handleEdit = () => {
    setMode('edit');
  };

  const handleDelete = () => {
    openModal('confirm', {
      title: 'Confirm Delete',
      content: (
        <Box>
          Are you sure you want to delete{' '}
          <Text bold inline>
            {partner!.name}
          </Text>
          ?
          <Box mt={4}>
            <InlineToast
              level="warning"
              message="All data owned by this partner will be permanently deleted, including identity
              provider connections and partner admins."
            />
          </Box>
        </Box>
      ),
      onClose: (confirm: boolean) => {
        if (confirm) {
          deletePartner(partnerId!)
            .then(({ deleted, error }) => {
              if (deleted) {
                setPartnerId('');
                onClose();
                toast('The partner was deleted');
                onChange && onChange();
                return;
              }

              errorToast(error || 'The partner was not deleted');
            })
            .catch(() => errorToast('The partner was not deleted'));
        }
      },
    });
  };

  const canDelete = !isNew && !partnerOrgs.length;
  const deleteTooltip = partner?.name.startsWith('SurePath AI')
    ? 'Is wrong! Newton Crosby, Ph.D not know this?!?'
    : 'You cannot delete a partner that has organizations';

  const panels: TabPanel[] = [
    {
      label: 'Details',
      icon: 'info-circle',
      path: 'details',
      component: <ReadonlyView fields={fields} data={getReadonlyData(partner)} />,
    },
    ...(isSuper && partner
      ? [
          {
            label: 'Partner Admins',
            icon: 'people',
            path: 'partner-admins',
            component: (
              <MembersPanel
                partner={partner}
                members={members}
                canAdd={isSuper}
                onChange={() => void membersQuery.refetch()}
              />
            ),
          } as TabPanel,
        ]
      : []),
    {
      label: 'Usage',
      icon: 'bar-chart',
      path: 'usage',
      component: <ListCard title="Organizations" icon="organization" items={partnerOrgs} />,
    },
  ];

  return (
    <Drawer open={open} onClose={onClose} className={className} loading={showLoader}>
      <DrawerHeader
        onClose={onClose}
        isTopDrawer={isTopDrawer}
        menu={
          !isNew ? (
            <DrawerMenu>
              <UpdateMenuItems
                onEdit={handleEdit}
                onDelete={handleDelete}
                canDelete={canDelete}
                deleteSubtitle={canDelete ? '' : deleteTooltip}
              />
            </DrawerMenu>
          ) : undefined
        }
      >
        {isNew ? 'Add Partner' : partner?.name}
      </DrawerHeader>
      <DrawerContent>
        {!inWriteMode && showChildren && <RouterTabs variant="drawer" panels={panels} />}

        <Box pt={2}>
          {inWriteMode && showChildren && (
            <Form formHandle={formHandle} query={query}>
              <DrawerFormSection label="Profile" />
              <Box mb={4}>
                <InputControl name="name" label="Name" formHandle={formHandle} autoFocus />

                <Box mt={1} mb={1}>
                  {isNew && (
                    <InputControl
                      name="domains"
                      label="Domain"
                      type="array-single"
                      formHandle={formHandle}
                    />
                  )}

                  {!isNew && (
                    <InputControl
                      name="domains"
                      label="Domains"
                      formHandle={formHandle}
                      type="list-editor"
                      height={90}
                      required
                    />
                  )}
                </Box>
              </Box>

              {isSuper && (
                <>
                  <DrawerFormSection label="Organization provisioning permissions" />
                  <Box mb={4}>
                    <Box mt={1} mb={2}>
                      <Text size="small" color="grey">
                        What this partner&apos;s users may do with organizations of each mode.
                      </Text>
                    </Box>
                    <OrgModesMatrix
                      orgModes={values.features.orgModes}
                      onChange={handleChangeOrgMode}
                    />
                  </Box>
                </>
              )}

              <DrawerButtons
                submit
                cancel
                isNew={isNew}
                canSubmit={canSubmit}
                canCancel={!isSubmitting}
                onSubmit={handleSubmit}
                onCancel={handleCancel}
              />
            </Form>
          )}
        </Box>
      </DrawerContent>
    </Drawer>
  );
};

export default PartnerDrawer;
